import { FC, useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Alert, Box, Button, Chip, Typography } from "@mui/material"
import { Block } from "@mui/icons-material"
import { GridColDef, GridRowParams } from "@mui/x-data-grid-pro"
import { DataGrid, Modal, SnackbarAlert } from "@/core/components"
import { useUrlPagination } from "@/core/hooks"
import { formatDateAndTime, getApiErrorMessage } from "@/core/utils"
import { GuestBot } from "../../interfaces"
import { useCancelJoinJobMutation, useJoinJobsListQuery } from "../../store"
import { useGuestBotWriteAccess } from "../../hooks"
import { TooltipAction } from "../common"
import {
  JOIN_JOB_STATUS_COLOR,
  JOIN_JOB_STATUS_LABEL,
} from "../../utils/chatStatus"

export const JoinJobsTable: FC = () => {
  const canWrite = useGuestBotWriteAccess()
  const navigate = useNavigate()
  const { page, pageSize, paginationModel, setPaginationModel } =
    useUrlPagination(50)
  const { data, isFetching, isError, error, refetch } = useJoinJobsListQuery({
    queryParams: { limit: pageSize, offset: page * pageSize },
  })
  const [cancelJoinJob, { isLoading: isCancelling }] =
    useCancelJoinJobMutation()
  const [toCancel, setToCancel] = useState<GuestBot.JoinJob | null>(null)
  const [snackbar, setSnackbar] = useState<{
    variant: "success" | "error"
    message: string
  } | null>(null)

  const columns: GridColDef<GuestBot.JoinJob>[] = [
    {
      field: "createdAt",
      headerName: "Создана",
      flex: 2,
      valueGetter: (params) => formatDateAndTime(params.row.createdAt),
    },
    {
      field: "status",
      headerName: "Статус",
      flex: 2,
      renderCell: (params) => (
        <Chip
          size="small"
          label={JOIN_JOB_STATUS_LABEL[params.row.status]}
          color={JOIN_JOB_STATUS_COLOR[params.row.status]}
        />
      ),
    },
    { field: "role", headerName: "Исполнитель", flex: 1 },
    {
      field: "linksSummary",
      headerName: "Ссылки",
      flex: 2,
      valueGetter: (params) => {
        const s = params.row.linksSummary
        return `Вступили ${s.joined} / ${s.total} · Заявки ${s.approvalPending}`
      },
    },
    { field: "lastError", headerName: "Ошибка", flex: 2 },
  ]

  const rowActions = useMemo(() => {
    const actions: ((
      params: GridRowParams<GuestBot.JoinJob>,
    ) => React.ReactElement)[] = []
    if (canWrite) {
      actions.push((params) =>
        params.row.status === "draft" ? (
          <TooltipAction
            icon={<Block />}
            label="Отменить"
            onClick={() => setToCancel(params.row)}
          />
        ) : (
          <></>
        ),
      )
    }
    return actions
  }, [canWrite])

  const handleCancel = useCallback(async () => {
    if (!toCancel) return
    try {
      await cancelJoinJob({ pathParams: { jobId: toCancel.id } }).unwrap()
      setSnackbar({ variant: "success", message: "Вступление отменено" })
    } catch (e) {
      setSnackbar({ variant: "error", message: getApiErrorMessage(e) })
    } finally {
      setToCancel(null)
    }
  }, [toCancel, cancelJoinJob])

  return (
    <>
      {isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Повторить
            </Button>
          }
        >
          {getApiErrorMessage(error)}
        </Alert>
      ) : !isFetching && (data?.total ?? 0) === 0 ? (
        <Typography>
          Вступлений пока нет.
          {/* CTA-подсказка только для тех, кто может создавать вступления. */}
          {canWrite && " Нажмите „Вступить в группы“."}
        </Typography>
      ) : (
        <Box height="70vh">
          <DataGrid<GuestBot.JoinJob>
            columns={columns}
            rows={data?.items ?? []}
            getRowId={(row) => row.id}
            loading={isFetching}
            paginationMode="server"
            rowCount={data?.total ?? 0}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pagination
            onRowClick={(params) =>
              navigate(`/telegram/guest-bots/chats/join-jobs/${params.row.id}`)
            }
            rowActions={rowActions}
          />
        </Box>
      )}

      {toCancel && (
        <Modal
          disableBackdropClose
          title="Отменить вступление?"
          onClose={() => setToCancel(null)}
          element={
            <Box>
              <Typography>
                Ожидающие ссылки будут отменены. Уже выполненные действия не
                изменятся.
              </Typography>
              <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={() => setToCancel(null)}>Не отменять</Button>
                <Button
                  color="error"
                  variant="contained"
                  disabled={isCancelling}
                  onClick={handleCancel}
                >
                  Отменить вступление
                </Button>
              </Box>
            </Box>
          }
        />
      )}
      <SnackbarAlert
        isOpen={!!snackbar}
        variant={snackbar?.variant}
        message={snackbar?.message ?? ""}
        onClose={() => setSnackbar(null)}
      />
    </>
  )
}
